import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Info, Paperclip, RefreshCw } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api, fmtDate, formatErr } from "@/lib/api";

const STATUS_STYLES = {
  sent: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border-emerald-500/40",
  failed: "bg-red-500/15 text-red-600 dark:text-red-300 border-red-500/40",
  queued: "bg-amber-500/15 text-amber-600 dark:text-amber-300 border-amber-500/40",
};

const Body = ({ m }) => (m.html
  ? <iframe title={m.subject} sandbox="" srcDoc={`<div style="font-family:Arial;font-size:14px;line-height:1.6">${m.html}</div>`} className="w-full h-64 rounded-xl bg-white" data-testid={`email-body-${m.id}`} />
  : <pre className="whitespace-pre-wrap font-sans text-sm rounded-xl bg-secondary p-4" data-testid={`email-body-${m.id}`}>{m.body}</pre>);

export const EmailLog = () => {
  const [emails, setEmails] = useState([]);
  const load = () => api.get("/admin/emails").then((r) => setEmails(r.data)).catch((e) => toast.error(formatErr(e)));
  useEffect(() => {
    load();
  }, []);
  const resend = async (id) => {
    try {
      await api.post(`/admin/emails/${id}/resend`);
      toast.success("Email re-queued");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <div data-testid="email-log">
      <div className="mb-5 flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[260px] flex items-start gap-3 rounded-2xl border border-primary/40 bg-primary/10 p-4 text-sm" data-testid="emails-delivery-notice">
          <Info className="h-5 w-5 text-primary shrink-0" />
          <p>All emails send from admin@imtheplugnow.com through your Resend account, with replies to the admin email and a BCC to the notification inbox.</p>
        </div>
        <button onClick={load} className="inline-flex h-10 items-center gap-2 px-4 rounded-full border border-border font-semibold text-sm hover:bg-secondary" data-testid="emails-refresh-button"><RefreshCw className="h-4 w-4" />Refresh</button>
      </div>
      {emails.length === 0 && <p className="text-muted-foreground">No emails yet.</p>}
      <Accordion type="multiple" className="space-y-2">
        {emails.map((m) => (
          <AccordionItem key={m.id} value={m.id} className="rounded-2xl border border-border bg-card px-5" data-testid={`email-item-${m.id}`}>
            <AccordionTrigger className="hover:no-underline text-left gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-bold truncate">{m.subject}</p>
                <p className="text-xs text-muted-foreground font-normal">To {m.to} · {fmtDate(m.created_at)} · {m.kind}{m.attachments?.length ? ` · ${m.attachments.length} attachment(s)` : ""}</p>
              </div>
              <span data-testid={`email-status-${m.id}`} className={`ml-auto shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-bold capitalize ${STATUS_STYLES[m.status] || "bg-muted"}`}>{m.status}</span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="text-xs text-muted-foreground space-y-0.5 mb-3">
                <p>From: {m.from} · Reply-To: {m.reply_to}</p>
                <p>To: {m.to}{m.bcc ? ` · BCC: ${m.bcc}` : ""}</p>
                {m.attachments?.map((a) => <p key={a.path} className="flex items-center gap-1"><Paperclip className="h-3 w-3" />{a.filename}</p>)}
                {m.error && <p className="text-destructive">Error: {m.error}</p>}
                {m.status === "failed" && <button onClick={() => resend(m.id)} className="mt-2 h-8 px-4 rounded-full bg-primary text-primary-foreground text-xs font-bold" data-testid={`email-resend-${m.id}`}>Resend</button>}
              </div>
              <Body m={m} />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};
